import { useSelector } from 'react-redux';

import type { RootStore } from '../../../types';

/**
 * Type and colour ramp for the customer dashboard tiles.
 *
 * The MUI theme's defaults give these tiles no usable hierarchy -- a heading
 * lands at rgb(94,94,95), the same mid-grey weight as the content under it, so
 * nothing reads as more or less important than anything else. These tokens set
 * the ramp the designs rely on: heading darkest, body a step lighter, and
 * supporting detail (timestamps, labels, handlers) clearly recessive.
 */
export interface DashboardPanelTokens {
  /** Tile headings and the values a reader scans for. */
  readonly ink: string;
  /** Primary content: activity summaries, claim descriptions. */
  readonly inkBody: string;
  /** Supporting detail: timestamps, field labels, handler lines. */
  readonly inkFaint: string;
  /** Links and status accents. */
  readonly brand: string;
  /** Tinted background behind a status pill. */
  readonly brandSurface: string;
  /** Inset panels within a tile. */
  readonly surfaceMuted: string;
  /** Rules and dividers. */
  readonly line: string;
  /** Good news: nothing outstanding. */
  readonly positive: string;
  /** Tinted ground behind a positive statement. */
  readonly positiveSurface: string;
  /** Something the reader must do before a feature works. */
  readonly caution: string;
  /** Tinted ground behind that prompt. */
  readonly cautionSurface: string;
}

const LIGHT: DashboardPanelTokens = {
  ink: '#0d1521',
  inkBody: '#33415c',
  inkFaint: '#8593aa',
  brand: '#0f62d8',
  brandSurface: '#e8f0fe',
  surfaceMuted: '#f7f9fc',
  line: '#e2e8f0',
  positive: '#1f8a56',
  positiveSurface: '#e7f5ee',
  caution: '#b7791f',
  cautionSurface: '#fdf6ec',
};

const DARK: DashboardPanelTokens = {
  ink: '#e8edf5',
  inkBody: '#c3ccdb',
  inkFaint: '#8e9aae',
  brand: '#5a9bff',
  brandSurface: 'rgba(90, 155, 255, 0.16)',
  surfaceMuted: 'rgba(255, 255, 255, 0.04)',
  line: '#333a47',
  positive: '#4caf7d',
  positiveSurface: 'rgba(76, 175, 125, 0.16)',
  caution: '#d9a441',
  cautionSurface: 'rgba(217, 164, 65, 0.14)',
};

export const getDashboardPanelTokens = (mode?: string): DashboardPanelTokens =>
  mode === 'dark' ? DARK : LIGHT;

export {
  rem,
  TILE_TITLE_FONT_SIZE,
  TILE_TITLE_FONT_WEIGHT,
  TILE_SUBTITLE_FONT_SIZE,
  TILE_SUBTITLE_GAP,
} from '../../../components/panels/panelLayout';

/**
 * The dashboard tiles' tokens for the current colour scheme.
 *
 * Reads redux, which is what actually drives dark mode here: `ThemeProvider`
 * takes `uiSettings.theme` and writes `data-mantine-color-scheme` from it.
 * Mantine's own colour-scheme state is never set -- nothing calls
 * `setColorScheme`, and the provider is mounted without one -- so
 * `useMantineColorScheme` always reports light, and reading it painted
 * light-mode ink onto a dark card.
 */
export const useDashboardPanelTokens = (): DashboardPanelTokens => {
  const mode = useSelector((state: RootStore) => state.uiSettings?.theme);
  return getDashboardPanelTokens(mode);
};
